import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@evocha/api-client";
import { CheckCircle2, Loader2, SendHorizontal, ShieldCheck } from "lucide-react";
import { dealerApi } from "../lib/api";
import { dateTime, toMinor, tzs, tzsPlain } from "../lib/format";
import { PageHead } from "../components/PageHead";

interface AgentRow {
  id: string;
  code: string;
  business_name: string;
  phone: string;
  status: string;
}

interface TransferRow {
  ref: string;
  description: string | null;
  posted_at: string | null;
}

const QUICK_AMOUNTS = [50_000, 100_000, 250_000, 500_000]; // minor units

function errText(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

export function SendFloat() {
  const queryClient = useQueryClient();
  const [agentId, setAgentId] = useState("");
  const [amountText, setAmountText] = useState("");
  const [pin, setPin] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<{ ref: string; replay: boolean } | null>(null);
  // Generated once per attempt so a retry after a network failure cannot
  // double-send; a fresh key is minted after each settled transfer.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const wallet = useQuery({
    queryKey: ["dealer-wallet"],
    queryFn: () =>
      dealerApi.get<{ available_minor: number; reserved_minor: number; commission_minor: number }>(
        "/superdealer/v1/wallet",
      ),
  });
  const agents = useQuery({
    queryKey: ["dealer-agents"],
    queryFn: () => dealerApi.get<{ data: AgentRow[]; total: number }>("/superdealer/v1/agents"),
  });
  const recent = useQuery({
    queryKey: ["dealer-transfers", 8],
    queryFn: () =>
      dealerApi.get<{ data: TransferRow[]; total: number }>("/superdealer/v1/transfers?limit=8"),
  });

  const activeAgents = useMemo(
    () => (agents.data?.data ?? []).filter((a) => a.status === "active"),
    [agents.data],
  );
  const selectedAgent = activeAgents.find((a) => a.id === agentId);
  const amountMinor = toMinor(amountText);

  const transfer = useMutation({
    mutationFn: () =>
      dealerApi.post<{ journal_ref: string; created: boolean }>("/superdealer/v1/transfers", {
        agent_id: agentId,
        amount_minor: amountMinor,
        idempotency_key: idempotencyKey,
        pin,
      }),
    onSuccess: (res) => {
      setDone({ ref: res.journal_ref, replay: !res.created });
      setAmountText("");
      setPin("");
      setIdempotencyKey(crypto.randomUUID());
      void queryClient.invalidateQueries({ queryKey: ["dealer-wallet"] });
      void queryClient.invalidateQueries({ queryKey: ["dealer-transfers"] });
      void queryClient.invalidateQueries({ queryKey: ["dealer-dashboard"] });
    },
    onError: () => undefined,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setDone(null);
    if (!agentId) return setFormError("Choose the agent who will receive the float.");
    if (!amountMinor || amountMinor <= 0) return setFormError("Enter a valid amount in TZS.");
    if (!/^\d{4,6}$/.test(pin)) return setFormError("Confirm with your 4–6 digit PIN.");
    transfer.mutate();
    return undefined;
  };

  return (
    <div>
      <PageHead title="Send float" description="Move wallet float to an agent instantly." />

      <div className="dealer-send-layout">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>New transfer</h3>
            <span className="ev-badge ev-badge-blue">Available {tzs(wallet.data?.available_minor)}</span>
          </div>

          {done && (
            <div className="dealer-alert dealer-alert-ok">
              <CheckCircle2 size={16} />
              <span>
                {done.replay
                  ? "This transfer was already processed."
                  : "Float sent successfully."}{" "}
                Reference <strong className="dash-mono">{done.ref}</strong>
              </span>
            </div>
          )}
          {transfer.isError && (
            <div className="dealer-alert dealer-alert-error">{errText(transfer.error)}</div>
          )}

          <form onSubmit={submit}>
            <label className="admin-field">
              <span>Recipient agent</span>
              <select
                className="ev-select"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                disabled={transfer.isPending}
              >
                <option value="">Select an agent…</option>
                {activeAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.business_name} · {a.code}
                  </option>
                ))}
              </select>
              {selectedAgent && (
                <span className="ev-body-micro muted">{selectedAgent.phone}</span>
              )}
            </label>

            <label className="admin-field">
              <span>Amount (TZS)</span>
              <input
                className="ev-input"
                inputMode="decimal"
                placeholder="e.g. 100,000"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                disabled={transfer.isPending}
              />
              <span className="ev-body-micro muted">
                {amountMinor ? `= ${tzsPlain(amountMinor)} TZS` : " "}
              </span>
            </label>

            <div className="dealer-chip-row">
              {QUICK_AMOUNTS.map((minor) => (
                <button
                  key={minor}
                  type="button"
                  className="dealer-chip"
                  onClick={() => setAmountText(String(minor / 100))}
                  disabled={transfer.isPending}
                >
                  {tzsPlain(minor)}
                </button>
              ))}
            </div>

            <label className="admin-field">
              <span>Confirm with PIN</span>
              <input
                className="ev-input"
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                disabled={transfer.isPending}
              />
            </label>

            {selectedAgent && amountMinor ? (
              <div className="dealer-review">
                <ShieldCheck size={16} />
                <span>
                  Send <strong>{tzs(amountMinor)}</strong> to{" "}
                  <strong>{selectedAgent.business_name}</strong> ({selectedAgent.code})
                </span>
              </div>
            ) : null}

            <button
              className="ev-btn ev-btn-primary ev-btn-lg"
              type="submit"
              disabled={transfer.isPending || !wallet.data}
            >
              {transfer.isPending ? <Loader2 size={18} className="spin" /> : <SendHorizontal size={18} />}
              {transfer.isPending ? "Sending…" : "Send float"}
            </button>
          </form>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Recent transfers</h3>
            <span className="ev-body-micro muted">{recent.data?.total ?? 0} total</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Reference</th>
                  <th className="admin-table-th">Description</th>
                  <th className="admin-table-th">Date</th>
                </tr>
              </thead>
              <tbody>
                {(recent.data?.data ?? []).map((t) => (
                  <tr key={t.ref}>
                    <td className="admin-table-td dash-mono">{t.ref.slice(0, 13)}</td>
                    <td className="admin-table-td">{t.description ?? "Float transfer"}</td>
                    <td className="admin-table-td ev-body-micro muted">{dateTime(t.posted_at)}</td>
                  </tr>
                ))}
                {!recent.isLoading && !recent.data?.data.length && (
                  <tr>
                    <td className="admin-table-td" colSpan={3}>
                      <div className="dash-table-empty">No transfers yet.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
